import { useEffect, useMemo, useState } from "react";
import { Plus, Package, ArrowUp, ArrowDown, Trash2, AlertCircle, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import { INVENTORY_CATEGORY_LABELS, INVENTORY_LOCATION_LABELS } from "@/lib/dictionaries";
import { useAuth } from "@/contexts/AuthContext";

export default function Inventory() {
    const { user } = useAuth();
    const canEditStock = ["admin", "coordinator", "supervisor", "super_admin"].includes(user?.role);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [movement, setMovement] = useState(null); // { item, qty, reason }
    const [editingStock, setEditingStock] = useState(null); // { id, value }
    const [savingStock, setSavingStock] = useState(false);
    const [form, setForm] = useState({
        sku: "",
        name: "",
        category: "cleaning",
        unit: "u",
        stock: 0,
        min_stock: 0,
        location: "main_warehouse",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/inventory").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const filtered = useMemo(() => {
        if (!search) return items;
        const s = search.toLowerCase();
        return items.filter((i) => i.name.toLowerCase().includes(s) || i.sku.toLowerCase().includes(s));
    }, [items, search]);

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        try {
            await api.post("/inventory", {
                ...form,
                stock: Number(form.stock),
                min_stock: Number(form.min_stock),
            });
            setOpen(false);
            setForm({ sku: "", name: "", category: "cleaning", unit: "u", stock: 0, min_stock: 0, location: "main_warehouse" });
            load();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const submitMovement = async () => {
        if (!movement?.item) return;
        const qty = Number(movement.qty);
        if (!qty) return;
        try {
            await api.post("/inventory/movements", {
                item_id: movement.item.id,
                qty,
                reason: movement.reason || "manual",
            });
            setMovement(null);
            load();
        } catch (err) {
            setError(formatApiError(err));
        }
    };

    const startEditStock = (item) => {
        if (!canEditStock) return;
        setEditingStock({ id: item.id, value: String(item.stock ?? 0) });
    };

    const saveStock = async (item) => {
        if (!editingStock || editingStock.id !== item.id) return;
        const next = Math.max(0, Math.floor(Number(editingStock.value)));
        if (Number.isNaN(next)) {
            setEditingStock(null);
            return;
        }
        if (next === Number(item.stock)) {
            setEditingStock(null);
            return;
        }
        setSavingStock(true);
        try {
            await api.post(`/inventory/${item.id}/set-stock`, { new_stock: next, reason: "recuento" });
            setEditingStock(null);
            load();
        } catch (err) {
            alert(formatApiError(err));
        } finally {
            setSavingStock(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar artículo y todos sus movimientos?")) return;
        await api.delete(`/inventory/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="inventory-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Almacén operativo
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        Inventario
                    </h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        {items.length} artículos · stock crítico marcado en rojo
                    </p>
                </div>
                <button className="zentra-btn-primary" onClick={() => setOpen(true)} data-testid="new-item-button">
                    <Plus size={16} /> Nuevo artículo
                </button>
            </div>

            <div className="zentra-card-static p-3">
                <input
                    className="zentra-input"
                    placeholder="Buscar por nombre o SKU…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="inventory-search"
                />
            </div>

            {loading ? (
                <div className="zentra-skeleton h-72" />
            ) : filtered.length === 0 ? (
                <EmptyState icon={Package} title="Sin artículos" description="Añade el primer artículo a tu inventario." />
            ) : (
                <div className="zentra-card-static overflow-hidden">
                    <table className="zentra-table">
                        <thead>
                            <tr>
                                <th>SKU</th>
                                <th>Artículo</th>
                                <th>Categoría</th>
                                <th>Ubicación</th>
                                <th className="text-right">Stock</th>
                                <th className="text-right">Mínimo</th>
                                <th className="text-right">Movimiento</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((it) => {
                                const critical = it.stock <= it.min_stock;
                                return (
                                    <tr key={it.id} className={critical ? "bg-red-50/60" : ""} data-testid={`item-row-${it.sku}`}>
                                        <td className="font-mono-zentra text-xs text-zentra-muted">{it.sku}</td>
                                        <td className="font-medium">
                                            {it.name}
                                            {critical && (
                                                <span className="inline-flex items-center gap-1 ml-2 text-[10px] uppercase tracking-wider text-red-700 font-semibold">
                                                    <AlertCircle size={11} /> crítico
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-sm text-zentra-muted">
                                            {INVENTORY_CATEGORY_LABELS[it.category] || it.category}
                                        </td>
                                        <td className="text-sm text-zentra-muted">
                                            {INVENTORY_LOCATION_LABELS[it.location] || it.location}
                                        </td>
                                        <td className="text-right font-display font-semibold">
                                            {editingStock && editingStock.id === it.id ? (
                                                <div className="inline-flex items-center gap-1 justify-end">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        autoFocus
                                                        className="w-20 px-2 py-1 border border-zentra-primary rounded-md text-right font-display font-semibold text-zentra-ink bg-white outline-none focus:ring-2 focus:ring-zentra-accent/30"
                                                        value={editingStock.value}
                                                        onChange={(e) => setEditingStock({ ...editingStock, value: e.target.value })}
                                                        onBlur={() => saveStock(it)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") saveStock(it);
                                                            if (e.key === "Escape") setEditingStock(null);
                                                        }}
                                                        disabled={savingStock}
                                                        data-testid={`stock-input-${it.sku}`}
                                                    />
                                                    <span className="text-[10px] text-zentra-muted">{it.unit || "u"}</span>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => startEditStock(it)}
                                                    disabled={!canEditStock}
                                                    className={`px-2 py-0.5 rounded-md transition ${
                                                        canEditStock
                                                            ? "hover:bg-zentra-accent/10 hover:text-zentra-accent-strong cursor-text"
                                                            : "cursor-default"
                                                    }`}
                                                    title={canEditStock ? "Pulsa para fijar el stock exacto" : ""}
                                                    data-testid={`stock-value-${it.sku}`}
                                                >
                                                    {it.stock}
                                                    <span className="ml-1 text-[10px] text-zentra-muted font-normal">{it.unit || "u"}</span>
                                                </button>
                                            )}
                                        </td>
                                        <td className="text-right text-zentra-muted">{it.min_stock}</td>
                                        <td className="text-right space-x-1">
                                            <button
                                                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-zentra-border bg-white text-emerald-700 hover:border-emerald-300"
                                                onClick={() => setMovement({ item: it, qty: 1, reason: "entrada" })}
                                                aria-label="Entrada"
                                                data-testid={`movement-in-${it.sku}`}
                                            >
                                                <ArrowUp size={14} />
                                            </button>
                                            <button
                                                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-zentra-border bg-white text-red-700 hover:border-red-300"
                                                onClick={() => setMovement({ item: it, qty: -1, reason: "consumo" })}
                                                aria-label="Salida"
                                                data-testid={`movement-out-${it.sku}`}
                                            >
                                                <ArrowDown size={14} />
                                            </button>
                                            <button
                                                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-red-200 bg-white text-red-700 hover:bg-red-50"
                                                onClick={() => remove(it.id)}
                                                aria-label="Eliminar"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* New item modal */}
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Nuevo artículo"
                wide
                testId="inventory-modal"
                footer={
                    <>
                        <button type="button" className="zentra-btn-ghost" onClick={() => setOpen(false)}>
                            Cancelar
                        </button>
                        <button type="submit" form="inv-form" className="zentra-btn-primary" disabled={saving} data-testid="save-item">
                            {saving ? "Guardando…" : "Crear"}
                        </button>
                    </>
                }
            >
                <form id="inv-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="zentra-label">SKU</label>
                        <input className="zentra-input" required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="item-sku" />
                    </div>
                    <div>
                        <label className="zentra-label">Categoría</label>
                        <select className="zentra-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                            {Object.entries(INVENTORY_CATEGORY_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>
                                    {v}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Nombre</label>
                        <input className="zentra-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name" />
                    </div>
                    <div>
                        <label className="zentra-label">Unidad</label>
                        <input className="zentra-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
                    </div>
                    <div>
                        <label className="zentra-label">Ubicación</label>
                        <select className="zentra-select" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}>
                            {Object.entries(INVENTORY_LOCATION_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>
                                    {v}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="zentra-label">Stock inicial</label>
                        <input type="number" className="zentra-input" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
                    </div>
                    <div>
                        <label className="zentra-label">Stock mínimo</label>
                        <input type="number" className="zentra-input" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} />
                    </div>
                    {error && <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
                </form>
            </Modal>

            {/* Movement modal */}
            <Modal
                open={!!movement}
                onClose={() => setMovement(null)}
                title={movement?.item ? `Movimiento · ${movement.item.name}` : ""}
                subtitle="Positivo = entrada, negativo = salida"
                testId="movement-modal"
                footer={
                    <>
                        <button className="zentra-btn-ghost" onClick={() => setMovement(null)}>
                            Cancelar
                        </button>
                        <button className="zentra-btn-primary" onClick={submitMovement} data-testid="save-movement">
                            Aplicar
                        </button>
                    </>
                }
            >
                {movement && (
                    <div className="space-y-3">
                        <div>
                            <label className="zentra-label">Cantidad</label>
                            <input
                                type="number"
                                className="zentra-input"
                                value={movement.qty}
                                onChange={(e) => setMovement({ ...movement, qty: e.target.value })}
                                data-testid="movement-qty"
                            />
                        </div>
                        <div>
                            <label className="zentra-label">Motivo</label>
                            <input
                                className="zentra-input"
                                value={movement.reason}
                                onChange={(e) => setMovement({ ...movement, reason: e.target.value })}
                            />
                        </div>
                        <p className="text-xs text-zentra-muted">
                            Stock actual: <strong className="text-zentra-ink">{movement.item.stock}</strong>
                            {" → "}
                            <strong className="text-zentra-primary">
                                {Math.max(0, movement.item.stock + Number(movement.qty || 0))}
                            </strong>
                        </p>
                    </div>
                )}
            </Modal>
        </div>
    );
}
